import { toast, type ToastVariant } from '@shared/hooks/useToast'

type MessageParams = Record<string, string | number>

type MessageTemplateValue = string | ((params: MessageParams) => string)

type MessageDefinition = {
  title: MessageTemplateValue
  description?: MessageTemplateValue
  variant?: ToastVariant
}

type MessageCatalog = typeof messageCatalog

export type UserMessageKey = keyof MessageCatalog

type MessageOverrides = {
  title?: string
  description?: string
  variant?: ToastVariant
}

type ErrorMessageResolutionOptions = {
  error: unknown
  defaultMessageKey?: UserMessageKey
  statusMap?: Partial<Record<number, UserMessageKey>>
  apiCodeMap?: Partial<Record<string, UserMessageKey>>
  fallbackTitle?: string
  fallbackDescription?: string
}

const messageCatalog = {
  'common.success.generic': {
    title: 'Operação realizada com sucesso.',
    variant: 'success',
  },
  'common.error.unexpected': {
    title: 'Não foi possível concluir a operação.',
    description: 'Tente novamente em instantes.',
    variant: 'destructive',
  },
  'common.error.network': {
    title: 'Falha de conexão.',
    description: 'Verifique sua internet e tente novamente.',
    variant: 'destructive',
  },
  'common.error.validation': {
    title: 'Alguns dados precisam de revisão.',
    description: 'Confira as informações e tente novamente.',
    variant: 'destructive',
  },
  'common.error.notFound': {
    title: 'Recurso não encontrado.',
    description: 'Atualize a página ou tente novamente em instantes.',
    variant: 'destructive',
  },
  'common.error.rateLimited': {
    title: 'Muitas tentativas em sequência.',
    description: 'Aguarde um instante antes de tentar novamente.',
    variant: 'destructive',
  },
  'common.error.serviceUnavailable': {
    title: 'Serviço temporariamente indisponível.',
    description: 'Tente novamente em instantes.',
    variant: 'destructive',
  },
  'auth.login.success': {
    title: 'Login realizado com sucesso!',
    description: (params) => `Bem-vindo(a), ${params.name}`,
    variant: 'success',
  },
  'auth.login.invalidCredentials': {
    title: 'Erro ao fazer login',
    description: 'E-mail ou senha inválidos.',
    variant: 'destructive',
  },
  'auth.login.serviceUnavailable': {
    title: 'Erro ao fazer login',
    description: 'Não foi possível acessar o serviço de login agora. Tente novamente em instantes.',
    variant: 'destructive',
  },
  'auth.login.network': {
    title: 'Erro ao fazer login',
    description: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.',
    variant: 'destructive',
  },
  'auth.login.rateLimited': {
    title: 'Erro ao fazer login',
    description: 'Muitas tentativas de login. Aguarde um instante e tente novamente.',
    variant: 'destructive',
  },
  'branding.update.success': {
    title: 'Branding atualizado.',
    description: 'As cores e a identidade visual do tenant foram aplicadas.',
    variant: 'success',
  },
  'branding.update.error': {
    title: 'Erro ao salvar branding.',
    description: 'Revise os dados e tente novamente.',
    variant: 'destructive',
  },
  'branding.upload.error': {
    title: 'Erro ao enviar logo.',
    description: 'Não foi possível concluir o upload agora. Tente novamente em instantes.',
    variant: 'destructive',
  },
} as const satisfies Record<string, MessageDefinition>

function resolveTemplateValue(value: MessageTemplateValue | undefined, params: MessageParams) {
  if (typeof value === 'function') {
    return value(params)
  }

  return value
}

function extractApiPayload(error: unknown) {
  if (
    typeof error !== 'object' ||
    error === null ||
    !('response' in error) ||
    typeof error.response !== 'object' ||
    error.response === null
  ) {
    return null
  }

  return error.response as {
    status?: number
    data?: {
      message?: string
      error?: { message?: string; code?: string }
      metadata?: { message?: string }
    }
  }
}

function extractApiMessage(error: unknown) {
  const payload = extractApiPayload(error)

  return payload?.data?.error?.message?.trim()
    || payload?.data?.metadata?.message?.trim()
    || payload?.data?.message?.trim()
}

function extractApiCode(error: unknown) {
  const payload = extractApiPayload(error)
  return payload?.data?.error?.code?.trim()
}

function extractStatusCode(error: unknown) {
  const payload = extractApiPayload(error)
  return payload?.status
}

function resolveMessage(
  key: UserMessageKey,
  options?: {
    params?: MessageParams
    overrides?: MessageOverrides
  },
) {
  const definition: MessageDefinition = messageCatalog[key]
  const params = options?.params ?? {}

  return {
    title: options?.overrides?.title ?? resolveTemplateValue(definition.title, params),
    description:
      options?.overrides?.description
      ?? resolveTemplateValue(definition.description, params),
    variant: options?.overrides?.variant ?? definition.variant ?? 'success',
  }
}

function showMessage(
  key: UserMessageKey,
  options?: {
    params?: MessageParams
    overrides?: MessageOverrides
  },
) {
  const message = resolveMessage(key, options)
  return toast(message)
}

function resolveErrorMessage({
  error,
  defaultMessageKey = 'common.error.unexpected',
  statusMap,
  apiCodeMap,
  fallbackTitle,
  fallbackDescription,
}: ErrorMessageResolutionOptions) {
  const apiCode = extractApiCode(error)
  if (apiCode && apiCodeMap?.[apiCode]) {
    return resolveMessage(apiCodeMap[apiCode])
  }

  const apiMessage = extractApiMessage(error)
  if (apiMessage) {
    return {
      ...resolveMessage(defaultMessageKey),
      title: fallbackTitle ?? resolveMessage(defaultMessageKey).title,
      description: apiMessage,
    }
  }

  const status = extractStatusCode(error)
  if (typeof status === 'number') {
    const mappedKey = statusMap?.[status]
      ?? defaultStatusMessageMap[status]

    if (mappedKey) {
      return resolveMessage(mappedKey)
    }
  }

  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === 'ERR_NETWORK'
  ) {
    return resolveMessage('common.error.network')
  }

  return {
    ...resolveMessage(defaultMessageKey),
    title: fallbackTitle ?? resolveMessage(defaultMessageKey).title,
    description: fallbackDescription ?? resolveMessage(defaultMessageKey).description,
  }
}

function showResolvedError(options: ErrorMessageResolutionOptions) {
  const message = resolveErrorMessage(options)
  return toast(message)
}

const defaultStatusMessageMap: Partial<Record<number, UserMessageKey>> = {
  400: 'common.error.validation',
  404: 'common.error.notFound',
  429: 'common.error.rateLimited',
  500: 'common.error.serviceUnavailable',
  502: 'common.error.serviceUnavailable',
  503: 'common.error.serviceUnavailable',
  504: 'common.error.serviceUnavailable',
}

export const userMessageManager = {
  catalog: messageCatalog,
  resolveMessage,
  showMessage,
  resolveErrorMessage,
  showResolvedError,
}
